import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, MapPin, CalendarDays, Send, Loader2, MessageSquare } from "lucide-react";

const STATUS_OPTIONS = ["new", "in_progress", "replied", "closed"];

export default function AdminMessages() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  // reply form state
  const [replyChannels, setReplyChannels] = useState({ sms: false, email: true });
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/messages");
      setMessages(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected) {
      setReplySubject(`Re: Your enquiry (${selected.reference})`);
      setReplyBody(`Hi ${selected.name?.split(" ")[0] || "there"},\n\nThanks for your enquiry. \n\n— Cheerings Plumbing Group`);
    }
  }, [selected?.id]);

  const filtered = messages.filter((m) => filter === "all" || m.status === filter);

  const updateStatus = async (id, status) => {
    try {
      const r = await authAxios.patch(`/messages/${id}/status`, { status });
      setMessages((arr) => arr.map((m) => (m.id === id ? r.data : m)));
      if (selected?.id === id) setSelected(r.data);
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const sendReply = async () => {
    if (!selected || sending) return;
    const channels = [];
    if (replyChannels.sms) channels.push("sms");
    if (replyChannels.email) channels.push("email");
    if (!channels.length) {
      toast({ title: "Choose at least one channel", variant: "destructive" });
      return;
    }
    if (!replyBody.trim()) {
      toast({ title: "Reply body required", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const r = await authAxios.post(`/messages/${selected.id}/reply`, {
        channels,
        subject: replySubject,
        body: replyBody,
      });
      setMessages((arr) => arr.map((m) => (m.id === selected.id ? r.data : m)));
      setSelected(r.data);
      toast({ title: "Reply sent", description: `via ${channels.join(" + ")}` });
    } catch (e) {
      toast({ title: "Reply failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">Messages &amp; Quote Enquiries</h1>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_500px] gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-600">
              {filtered.length} enquiries
            </div>
            <div className="divide-y divide-slate-100 max-h-[75vh] overflow-y-auto">
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  className={`w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors ${selected?.id === m.id ? "bg-sky-50" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-slate-800">{m.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "new" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"}`}>{m.status}</span>
                  </div>
                  <div className="text-xs text-slate-500">{m.reference} • {m.service_title}</div>
                  <div className="text-xs text-slate-600 mt-1 line-clamp-2">{m.message}</div>
                </button>
              ))}
              {filtered.length === 0 && <div className="px-5 py-8 text-sm text-slate-500">No enquiries.</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 h-fit">
            {!selected ? (
              <div className="text-sm text-slate-500 py-10 text-center">Select an enquiry to view and reply.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{selected.reference}</div>
                    <div className="font-extrabold text-lg text-slate-900">{selected.name}</div>
                  </div>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Info icon={Phone} label="Phone" value={selected.phone} />
                <Info icon={Mail} label="Email" value={selected.email || "—"} />
                <Info icon={MapPin} label="Address" value={`${selected.address || "—"}, ${selected.suburb || "—"}`} />
                <Info icon={CalendarDays} label="Preferred date" value={selected.preferred_date || "—"} />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Service</div>
                  <div className="text-sm text-slate-800">{selected.service_title}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Message</div>
                  <div className="text-sm text-slate-700 bg-slate-50 rounded p-3 whitespace-pre-wrap">{selected.message}</div>
                </div>
                {selected.image_urls?.length > 0 && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Attached photos</div>
                    <div className="grid grid-cols-3 gap-2">
                      {selected.image_urls.map((u) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer" className="block aspect-square rounded overflow-hidden border">
                          <img src={u} alt="attachment" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Replies thread */}
                {selected.replies?.length > 0 && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Reply history</div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selected.replies.map((r) => (
                        <div key={r.id} className="bg-sky-50 border border-sky-100 rounded p-3 text-xs">
                          <div className="flex justify-between text-slate-600 mb-1">
                            <span><strong>{r.by}</strong> via {r.channels.join(" + ")}</span>
                            <span>{r.at?.slice(0, 16)}</span>
                          </div>
                          <div className="text-slate-800 whitespace-pre-wrap">{r.body}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reply form */}
                <div className="pt-3 border-t mt-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-2">
                    <MessageSquare size={16} /> Send reply
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={replyChannels.sms} onCheckedChange={(v) => setReplyChannels((c) => ({ ...c, sms: !!v }))} /> SMS
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={replyChannels.email} onCheckedChange={(v) => setReplyChannels((c) => ({ ...c, email: !!v }))} /> Email
                    </label>
                  </div>
                  {replyChannels.email && (
                    <div className="mb-2">
                      <Label className="text-xs font-semibold text-slate-600">Subject</Label>
                      <Input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} className="mt-1 h-10" />
                    </div>
                  )}
                  <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={5} className="mb-3" />
                  <Button onClick={sendReply} disabled={sending} className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold">
                    {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Send reply</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-sky-600 mt-0.5" />
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-all">{value}</div>
      </div>
    </div>
  );
}
